import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getReceiptByNumber, updateReceipt, deleteReceipt, getAllReceiptsAllPages } from '../api/receipts';
import debtorsService from '../api/debtors';
import { getFromCache } from '../services/cacheService';
import { getItemName } from '../utils/itemHelpers';
import Navbar from '../components/Navbar';
import '../styles/Receipts.css';

const Receipts = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [receiptNumber, setReceiptNumber] = useState('');
  const [receipt, setReceipt] = useState(null);
  const [allReceipts, setAllReceipts] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingItems, setIsEditingItems] = useState(false);
  const [editData, setEditData] = useState({ payment_method: '', notes: '' });
  const [editItems, setEditItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSearch, setShowSearch] = useState(true);
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [invoiceFilter, setInvoiceFilter] = useState('all');
  const [customerSearch, setCustomerSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  const getSaleItemDisplayName = (item) => {
    if (i18n.language === 'ar') return item.item_name_ar || item.item_name;
    if (i18n.language === 'fr') return item.item_name_fr || item.item_name;
    return item.item_name;
  };

  const getWholesaleLabel = () => {
    const label = t('wholesale');
    if (label && label !== 'wholesale') return label;
    if (i18n.language === 'ar') return 'جملة';
    if (i18n.language === 'fr') return 'Vente en gros';
    return 'Wholesale';
  };

  const getWholesaleInvoiceLabel = () => {
    const label = t('wholesaleInvoice');
    if (label && label !== 'wholesaleInvoice') return label;
    if (i18n.language === 'ar') return 'فاتورة جملة';
    if (i18n.language === 'fr') return 'Facture de gros';
    return 'Wholesale Invoice';
  };

  const enrichReceiptWithDebtor = async (receiptData) => {
    if (!receiptData) return receiptData;
    if (receiptData.customer_name || receiptData.debtor_name) return receiptData;
    const debtorId = receiptData.customer || receiptData.debtor;
    if (!debtorId) return receiptData;

    try {
      const response = await debtorsService.getDebtor(debtorId);
      const debtor = response.data;
      if (debtor) {
        return {
          ...receiptData,
          customer_name: debtor.name,
          customer_phone: debtor.phone,
          debtor_name: debtor.name,
          debtor_phone: debtor.phone
        };
      }
    } catch (error) {
      console.error('Error fetching debtor info:', error);
    }
    return receiptData;
  };

  // Fetch all receipts on load
  useEffect(() => {
    const fetchReceipts = async () => {
      try {
        const response = await getAllReceiptsAllPages();
        const receiptsArray = Array.isArray(response) ? response : response?.data?.results || response?.data || [];
        let normalizedReceipts = Array.isArray(receiptsArray) ? receiptsArray : [];
        const needsCustomerInfo = normalizedReceipts.some(r => !r.customer_name && !r.debtor_name && (r.customer || r.debtor));
        if (needsCustomerInfo) {
          try {
            const debtorsResponse = await debtorsService.getDebtors();
            const debtors = debtorsResponse.data || [];
            const debtorMap = new Map(debtors.map(d => [d.id, d]));
            normalizedReceipts = normalizedReceipts.map(r => {
              if (r.customer_name || r.debtor_name) return r;
              const debtorId = r.customer || r.debtor;
              const debtor = debtorMap.get(debtorId);
              if (!debtor) return r;
              return {
                ...r,
                customer_name: debtor.name,
                customer_phone: debtor.phone,
                debtor_name: debtor.name,
                debtor_phone: debtor.phone
              };
            });
          } catch (error) {
            console.error('Error fetching debtors:', error);
          }
        }
        setAllReceipts(normalizedReceipts);
      } catch (error) {
        console.error('Error fetching receipts:', error);
        // Try to use cached receipts
        if (!navigator.onLine) {
          const cachedReceipts = await getFromCache('RECEIPTS');
          if (cachedReceipts) {
            const receiptsArray = Array.isArray(cachedReceipts) ? cachedReceipts : cachedReceipts.results || [];
            setAllReceipts(receiptsArray);
            toast.info('Using offline data (cached receipts)');
          }
        }
      }
    };
    fetchReceipts();
  }, []);

  const handleSearchReceipt = async (e) => {
    e.preventDefault();
    if (!receiptNumber.trim()) {
      toast.error(t('pleaseEnterReceiptNumber') || 'Please enter a receipt number');
      return;
    }

    setLoading(true);
    try {
      const response = await getReceiptByNumber(receiptNumber);
      const enrichedReceipt = await enrichReceiptWithDebtor(response.data);
      setReceipt(enrichedReceipt);
      setEditData({
        payment_method: response.data.payment_method,
        notes: response.data.notes || ''
      });
      setEditItems(response.data.items.map(item => ({ ...item })));
      setIsEditing(false);
      setIsEditingItems(false);
      setShowSearch(false);
    } catch (error) {
      toast.error(`${t('receiptNotFound') || 'Receipt not found'}: #${receiptNumber}`);
      setReceipt(null);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateReceipt = async () => {
    setLoading(true);
    try {
      const updatePayload = { ...editData };
      
      // Include items if editing items
      if (isEditingItems) {
        updatePayload.items = editItems.map(item => ({
          id: item.id,
          quantity: (() => {
            const unitType = item.item_unit_type || 'quantity';
            const allowFractional = !!item.item_allow_fractional || unitType !== 'quantity';
            if (allowFractional) return parseFloat(item.quantity) || item.quantity;
            return parseInt(item.quantity, 10) || item.quantity;
          })(),
          price_usd: parseFloat(item.price_usd) || item.price_usd,
          price_lbp: parseFloat(item.price_lbp) || item.price_lbp
        }));
      }
      
      const response = await updateReceipt(receipt.receipt_number, updatePayload);
      setReceipt(response.data);
      setEditItems(response.data.items.map(item => ({ ...item })));
      setAllReceipts(prev => prev.map(r =>
        r.receipt_number === response.data.receipt_number
          ? { ...r, ...response.data }
          : r
      ));
      setIsEditing(false);
      setIsEditingItems(false);
      toast.success(`${t('receiptUpdated') || 'Receipt updated'}: #${receipt.receipt_number}`);
    } catch (error) {
      console.error('Update error:', error);
      toast.error(error.response?.data?.error || t('errorUpdatingReceipt') || 'Error updating receipt');
    } finally {
      setLoading(false);
    }
  };

  const handlePrintReceipt = () => {
    if (!receipt) return;

    // Calculate totals with exchange rate
    let exchangeRate = 1;
    if (receipt.items && receipt.items.length > 0) {
      exchangeRate = parseFloat(receipt.items[0].exchange_rate || 1);
    }

    const totalUSD = parseFloat(receipt.total_usd || 0);
    const totalLBP = parseFloat(receipt.total_lbp || 0);
    const grandTotalUSD = totalUSD + (totalLBP / exchangeRate);
    const grandTotalLBP = (totalUSD * exchangeRate) + totalLBP;

    const printWindow = window.open('', '', 'height=600,width=800');
    const html = `
      <!DOCTYPE html>
      <html dir="${i18n.language === 'ar' ? 'rtl' : 'ltr'}">
      <head>
        <meta charset="UTF-8">
        <title>${t('receipt') || 'Receipt'} #${receipt.receipt_number}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 20px;
            direction: ${i18n.language === 'ar' ? 'rtl' : 'ltr'};
          }
          .receipt-container {
            max-width: 600px;
            margin: 0 auto;
            border: 1px solid #ccc;
            padding: 20px;
          }
          .receipt-header {
            text-align: center;
            margin-bottom: 20px;
            border-bottom: 2px solid #333;
            padding-bottom: 10px;
          }
          .receipt-header h1 {
            margin: 0;
          }
          .receipt-info {
            margin-bottom: 15px;
            font-size: 14px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin: 15px 0;
          }
          th, td {
            border: 1px solid #ddd;
            padding: 8px;
            text-align: ${i18n.language === 'ar' ? 'right' : 'left'};
          }
          th {
            background-color: #f5f5f5;
            font-weight: bold;
          }
          .totals {
            margin-top: 20px;
            padding: 15px;
            border-top: 2px solid #333;
            border-radius: 5px;
            background-color: #f9f9f9;
          }
          .total-row {
            display: flex;
            justify-content: space-between;
            margin: 10px 0;
            font-size: 16px;
            padding: 8px 0;
            border-bottom: 1px solid #ddd;
          }
          .total-row.primary {
            font-weight: bold;
            background-color: #ecf0f1;
            padding: 10px;
            border-radius: 3px;
          }
          .total-row.grand {
            font-weight: bold;
            font-size: 18px;
            background-color: #fff3cd;
            border: 2px solid #ffc107;
            padding: 10px;
            border-radius: 3px;
          }
          .divider {
            height: 2px;
            background: #333;
            margin: 10px 0;
          }
          .footer {
            text-align: center;
            margin-top: 20px;
            font-size: 12px;
            color: #999;
          }
          @media print {
            body {
              margin: 0;
            }
          }
          [dir="rtl"] .receipt-info p {
            text-align: right;
            direction: rtl;
          }
          [dir="rtl"] .total-row {
            direction: rtl;
            text-align: right;
          }
          [dir="rtl"] table {
            direction: rtl;
          }
          [dir="rtl"] table th,
          [dir="rtl"] table td {
            text-align: right;
          }
        </style>
      </head>
      <body ${i18n.language === 'ar' ? 'dir="rtl"' : ''}>
        <div class="receipt-container">
          <div class="receipt-header">
            <h1>${t('receipt') || 'Receipt'}</h1>
            <p>#${receipt.receipt_number}</p>
          </div>

          <div class="receipt-info">
            <p><strong>${t('date')}:</strong> ${new Date(receipt.sale_date).toLocaleString(i18n.language)}</p>
            <p><strong>${t('seller')}:</strong> ${receipt.seller_name}</p>
            ${(receipt.customer_name || receipt.debtor_name) ? `<p><strong>${t('customer')}:</strong> ${(receipt.customer_name || receipt.debtor_name)}${(receipt.customer_phone || receipt.debtor_phone) ? ` - ${(receipt.customer_phone || receipt.debtor_phone)}` : ''}</p>` : ''}
            <p><strong>${t('paymentMethod')}:</strong> ${t(receipt.payment_method)}</p>
            ${receipt.notes ? `<p><strong>${t('notes')}:</strong> ${receipt.notes}</p>` : ''}
          </div>

          <table>
            <thead>
              <tr>
                <th>${t('item')}</th>
                <th>${t('qty')}</th>
                <th>${t('price')}</th>
                <th>${t('total')}</th>
              </tr>
            </thead>
            <tbody>
              ${receipt.items.map(item => {
                const qty = parseFloat(item.quantity) || 0;
                const qtyDisplay = qty % 1 === 0 ? qty.toFixed(0) : qty.toFixed(3).replace(/\.?0+$/, '');
                const localizedItemName = i18n.language === 'ar'
                  ? (item.item_name_ar || item.item_name)
                  : i18n.language === 'fr'
                    ? (item.item_name_fr || item.item_name)
                    : item.item_name;
                return `
                <tr>
                  <td>${localizedItemName}</td>
                  <td>${qtyDisplay}</td>
                  <td>
                    ${item.price_usd > 0 ? '$' + item.price_usd.toLocaleString('en-US', { minimumFractionDigits: 2 }) : ''}
                    ${item.price_lbp > 0 ? (item.price_usd > 0 ? ' / ' : '') + item.price_lbp.toLocaleString('en-US', { minimumFractionDigits: 2 }) + ' ل.ل' : ''}
                  </td>
                  <td>
                    ${item.subtotal_usd > 0 ? '$' + item.subtotal_usd.toLocaleString('en-US', { minimumFractionDigits: 2 }) : ''}
                    ${item.subtotal_lbp > 0 ? (item.subtotal_usd > 0 ? ' / ' : '') + item.subtotal_lbp.toLocaleString('en-US', { minimumFractionDigits: 2 }) + ' ل.ل' : ''}
                  </td>
                </tr>
              `;
              }).join('')}
            </tbody>
          </table>

          <div class="totals">
            ${totalUSD > 0 ? `
              <div class="total-row primary">
                <strong>${t('totalUSD')}:</strong>
                <strong>$${totalUSD.toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
              </div>
            ` : ''}
            ${totalLBP > 0 ? `
              <div class="total-row primary">
                <strong>${t('totalLBP')}:</strong>
                <strong>${totalLBP.toLocaleString('en-US', { minimumFractionDigits: 2 })} ل.ل</strong>
              </div>
            ` : ''}
            
            ${(totalUSD > 0 || totalLBP > 0) ? `
              <div class="divider"></div>
              <div class="total-row grand">
                <strong>${t('grandTotal')}:</strong>
                <strong>$${grandTotalUSD.toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
              </div>
              <div class="total-row grand">
                <strong>${t('grandTotal')}:</strong>
                <strong>${grandTotalLBP.toLocaleString('en-US', { minimumFractionDigits: 2 })} ل.ل</strong>
              </div>
            ` : ''}
          </div>

          <div class="footer">
            <p>${t('generatedOn') || 'Generated on'}: ${new Date().toLocaleString(i18n.language)}</p>
          </div>
        </div>
        <script>
          window.print();
        </script>
      </body>
      </html>
    `;
    printWindow.document.write(html);
    printWindow.document.close();
  };

  const handleDeleteReceipt = async () => {
    if (!window.confirm(t('confirmDeleteReceipt') || `Are you sure you want to delete receipt #${receipt.receipt_number}?`)) {
      return;
    }

    setLoading(true);
    try {
      const receiptNum = receipt.receipt_number;
      await deleteReceipt(receiptNum);
      toast.success(`${t('receiptDeleted') || 'Receipt deleted'}: #${receiptNum}`);
      setReceipt(null);
      setReceiptNumber('');
      setShowSearch(true);
      setAllReceipts(allReceipts.filter(r => r.receipt_number !== receiptNum));
    } catch (error) {
      toast.error(t('errorDeletingReceipt') || 'Error deleting receipt');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectReceipt = async (r) => {
    const enrichedReceipt = await enrichReceiptWithDebtor(r);
    setReceiptNumber(String(enrichedReceipt.receipt_number));
    setReceipt(enrichedReceipt);
    setEditData({
      payment_method: enrichedReceipt.payment_method,
      notes: enrichedReceipt.notes || ''
    });
    setIsEditing(false);
    setIsEditingItems(false);
    setEditItems((enrichedReceipt.items || []).map(item => ({ ...item })));
    setShowSearch(false);
  };

  const filteredReceipts = allReceipts.filter(r => {
    // Apply payment filter
    if (paymentFilter !== 'all') {
      if (paymentFilter === 'debt' && r.payment_method !== 'debt') {
        return false;
      }
      if (paymentFilter === 'cash' && r.payment_method !== 'cash') {
        return false;
      }
      if (paymentFilter === 'card' && r.payment_method !== 'card') {
        return false;
      }
      if (paymentFilter === 'both' && r.payment_method !== 'both') {
        return false;
      }
    }

    // Apply invoice type filter
    if (invoiceFilter !== 'all') {
      if (invoiceFilter === 'wholesale' && !r.is_wholesale) {
        return false;
      }
      if (invoiceFilter === 'retail' && r.is_wholesale) {
        return false;
      }
    }

    // Apply unified text search: customer/debtor name OR item name
    if (customerSearch) {
      const query = customerSearch.toLowerCase();
      const customerName = (r.customer_name || r.debtor_name || '').toLowerCase();
      const customerMatches = customerName.includes(query);
      const itemMatches = Array.isArray(r.items) && r.items.some(item => {
        const itemName = (item.item_name_ar || item.item_name_fr || item.item_name || '').toLowerCase();
        return itemName.includes(query);
      });

      if (!customerMatches && !itemMatches) {
        return false;
      }
    }

    // Apply date range filter
    if (startDate || endDate) {
      const receiptDate = new Date(r.sale_date);
      receiptDate.setHours(0, 0, 0, 0);

      if (startDate) {
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        if (receiptDate < start) {
          return false;
        }
      }

      if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        if (receiptDate > end) {
          return false;
        }
      }
    }

    return true;
  });

  return (
    <div className="receipts-container" dir={i18n.dir()}>
      <Navbar />
      
      <div className="receipts-header" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px' }}>
        <h1>{t('receipts') || 'Receipts'}</h1>
      </div>

      <div className="receipts-content">
        {!receipt && (
          <div className="search-section">
            <h2>{t('searchReceipt') || 'Search Receipt'}</h2>
            <form onSubmit={handleSearchReceipt} className="search-form">
              <div className="form-group">
                <label>{t('receiptNumber')}:</label>
                <input
                  type="number"
                  min="1"
                  value={receiptNumber}
                  onChange={(e) => setReceiptNumber(e.target.value)}
                  placeholder={t('enterReceiptNumber') || 'Enter receipt number'}
                  className="form-input"
                />
              </div>
              <button type="submit" disabled={loading} className="search-btn">
                {loading ? `${t('loading')}...` : t('search')}
              </button>
            </form>

            {allReceipts.length > 0 && (
              <div className="recent-receipts">
                {/* Search and Filter Section */}
                <div className="filter-section">
                  <input
                    type="text"
                    placeholder={
                      i18n.language === 'ar'
                        ? 'ابحث باسم الزبون أو العنصر'
                        : i18n.language === 'fr'
                          ? 'Rechercher par client ou article'
                          : 'Search by customer or item'
                    }
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    className="filter-input"
                  />
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    placeholder={t('fromDate') || 'From Date'}
                    className="filter-input date-input"
                  />
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    placeholder={t('toDate') || 'To Date'}
                    className="filter-input date-input"
                  />
                  
                  <select
                    value={paymentFilter}
                    onChange={(e) => setPaymentFilter(e.target.value)}
                    className="filter-input"
                  >
                    <option value="all">{t('allPayments') || 'All Payments'}</option>
                    <option value="debt">{i18n.language === 'ar' ? 'دين' : i18n.language === 'fr' ? 'Crédit' : 'Debt'}</option>
                    <option value="cash">{i18n.language === 'ar' ? 'نقدي' : i18n.language === 'fr' ? 'Espèces' : 'Cash'}</option>
                    <option value="card">{i18n.language === 'ar' ? 'بطاقة' : i18n.language === 'fr' ? 'Carte' : 'Card'}</option>
                    <option value="both">{i18n.language === 'ar' ? 'كلاهما' : i18n.language === 'fr' ? 'Les deux' : 'Both'}</option>
                  </select>
                  
                  <select
                    value={invoiceFilter}
                    onChange={(e) => setInvoiceFilter(e.target.value)}
                    className="filter-input"
                  >
                    <option value="all">{t('allInvoices') || 'All Invoices'}</option>
                    <option value="wholesale">{i18n.language === 'ar' ? 'جملة' : i18n.language === 'fr' ? 'Gros' : 'Wholesale'}</option>
                    <option value="retail">{i18n.language === 'ar' ? 'مفرق' : i18n.language === 'fr' ? 'Détail' : 'Retail'}</option>
                  </select>
                  
                  {(customerSearch || startDate || endDate || paymentFilter !== 'all' || invoiceFilter !== 'all') && (
                    <button 
                      onClick={() => {
                        setCustomerSearch('');
                        setStartDate('');
                        setEndDate('');
                        setPaymentFilter('all');
                        setInvoiceFilter('all');
                      }}
                      className="clear-filters-btn"
                    >
                      {t('clearFilters') || 'Clear'}
                    </button>
                  )}
                </div>
                
                <h3>
                  {t('receipts') || 'Receipts'}
                  <span className="receipts-count-badge">
                    {i18n.language === 'ar' ? 'العدد' : i18n.language === 'fr' ? 'Nombre' : 'Count'}: {filteredReceipts.length}
                  </span>
                </h3>
                <div className="receipts-list">
                  {filteredReceipts.map(r => {
                    // Calculate total in USD for this receipt
                    let exchangeRate = 1;
                    if (r.items && r.items.length > 0) {
                      exchangeRate = parseFloat(r.items[0].exchange_rate || 1);
                    }
                    const totalInUSD = parseFloat(r.total_usd || 0) + (parseFloat(r.total_lbp || 0) / exchangeRate);
                    
                    return (
                      <button
                        key={r.id}
                        className="receipt-item"
                        onClick={() => handleSelectReceipt(r)}
                        title={`${t('receipt')} #${r.receipt_number}`}
                      >
                        <div className="receipt-info">
                          <div className="receipt-num">#{r.receipt_number}</div>
                          {r.is_edited && (
                            <div className="receipt-type-badge edited">
                              {i18n.language === 'ar' ? 'معدلة' : i18n.language === 'fr' ? 'Modifiee' : 'Edited'}
                            </div>
                          )}
                          {r.is_wholesale && (
                            <div className="receipt-type-badge wholesale">
                              {getWholesaleLabel()}
                            </div>
                          )}
                          <div className="receipt-date">{new Date(r.sale_date).toLocaleDateString(i18n.language)}</div>
                          {(r.customer_name || r.debtor_name) && (
                            <div className="receipt-customer">
                              {t('customer')}: {r.customer_name || r.debtor_name}
                            </div>
                          )}
                          <div className="receipt-amount">
                            ${totalInUSD.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {receipt && (
          <div className="receipt-detail">
            <div className="receipt-actions">
              <button 
                onClick={() => {
                  setReceipt(null);
                  setReceiptNumber('');
                  setIsEditing(false);
                  setIsEditingItems(false);
                  setEditItems([]);
                }} 
                className="back-btn"
                style={{ marginBottom: '10px' }}
              >
                {t('back') || 'Back'}
              </button>
              {!isEditing && !isEditingItems && (
                <>
                  <button onClick={() => {
                    setIsEditing(true);
                    setIsEditingItems(true);
                    setEditItems(receipt.items.map(item => ({ ...item })));
                  }} className="edit-btn">
                    {t('edit')}
                  </button>
                  <button onClick={handlePrintReceipt} className="print-btn">
                    {t('print') || 'Print'}
                  </button>
                  <button onClick={handleDeleteReceipt} disabled={loading} className="delete-btn">
                    {t('delete')}
                  </button>
                </>
              )}
            </div>

            <div className="receipt-viewer">
              <div className="receipt-header-info">
                <h2>{t('receipt')} #{receipt.receipt_number}</h2>
                {receipt.is_edited && (
                  <p className="receipt-edited-label">
                    {i18n.language === 'ar' ? 'فاتورة معدلة' : i18n.language === 'fr' ? 'Recu modifie' : 'Edited Receipt'}
                  </p>
                )}
                {receipt.is_wholesale && (
                  <p className="receipt-wholesale-label">{getWholesaleInvoiceLabel()}</p>
                )}
                <p>{t('date')}: {new Date(receipt.sale_date).toLocaleString(i18n.language)}</p>
                <p>{t('seller')}: {receipt.seller_name}</p>
                {(receipt.customer_name || receipt.debtor_name) && (
                  <p>{t('customer')}: {receipt.customer_name || receipt.debtor_name}{(receipt.customer_phone || receipt.debtor_phone) ? ` - ${receipt.customer_phone || receipt.debtor_phone}` : ''}</p>
                )}
              </div>

              {/* Receipt Items */}
              <div className="receipt-items">
                <h3>{t('items')}</h3>
                {isEditingItems ? (
                  <table>
                    <thead>
                      <tr>
                        <th>{t('item')}</th>
                        <th>{t('qty')}</th>
                        <th>{t('price')}</th>
                        <th>{t('total')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {editItems && Array.isArray(editItems) && editItems.map(item => (
                        <tr key={item.id}>
                          <td>{getSaleItemDisplayName(item)}</td>
                          <td>
                            {(() => {
                              const unitType = item.item_unit_type || 'quantity';
                              const allowFractional = !!item.item_allow_fractional || unitType !== 'quantity';
                              return (
                            <input
                              type="number"
                              min={allowFractional ? "0.01" : "1"}
                              step={allowFractional ? "0.01" : "1"}
                              value={item.quantity}
                              onChange={(e) => {
                                const value = allowFractional
                                  ? (parseFloat(e.target.value) || 0)
                                  : (parseInt(e.target.value, 10) || 0);
                                const newItems = editItems.map(i =>
                                  i.id === item.id
                                    ? { ...i, quantity: value <= 0 ? (allowFractional ? 0.01 : 1) : value }
                                    : i
                                );
                                setEditItems(newItems);
                              }}
                              className="edit-quantity-input"
                            />
                              );
                            })()}
                          </td>
                          <td>
                            {item.price_usd > 0 && <div>${item.price_usd.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>}
                            {item.price_lbp > 0 && <div>{item.price_lbp.toLocaleString('en-US', { minimumFractionDigits: 2 })} {t('lbpSymbol')}</div>}
                          </td>
                          <td>
                            {item.subtotal_usd > 0 && <div>${(item.price_usd * item.quantity).toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>}
                            {item.subtotal_lbp > 0 && <div>{(item.price_lbp * item.quantity).toLocaleString('en-US', { minimumFractionDigits: 2 })} {t('lbpSymbol')}</div>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <table>
                    <thead>
                      <tr>
                        <th>{t('item')}</th>
                        <th>{t('qty')}</th>
                        <th>{t('price')}</th>
                        <th>{t('total')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {receipt.items && Array.isArray(receipt.items) && receipt.items.map(item => {
                        const qty = parseFloat(item.quantity) || 0;
                        const qtyDisplay = qty % 1 === 0 ? qty.toFixed(0) : qty.toFixed(3).replace(/\.?0+$/, '');
                        return (
                        <tr key={item.id}>
                          <td>{getSaleItemDisplayName(item)}</td>
                          <td>{qtyDisplay}</td>
                          <td>
                            {item.price_usd > 0 && <div>${item.price_usd.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>}
                            {item.price_lbp > 0 && <div>{item.price_lbp.toLocaleString('en-US', { minimumFractionDigits: 2 })} {t('lbpSymbol')}</div>}
                          </td>
                          <td>
                            {item.subtotal_usd > 0 && <div>${item.subtotal_usd.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>}
                            {item.subtotal_lbp > 0 && <div>{item.subtotal_lbp.toLocaleString('en-US', { minimumFractionDigits: 2 })} {t('lbpSymbol')}</div>}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Receipt Totals */}
              <div className="receipt-totals">
                {(() => {
                  // Get exchange rate from receipt items
                  let exchangeRate = 1;
                  if (receipt.items && receipt.items.length > 0) {
                    exchangeRate = parseFloat(receipt.items[0].exchange_rate || 1);
                  }

                  const totalUSD = parseFloat(receipt.total_usd || 0);
                  const totalLBP = parseFloat(receipt.total_lbp || 0);
                  const grandTotalLBP = (totalUSD * exchangeRate) + totalLBP;
                  const grandTotalUSD = totalUSD + (totalLBP / exchangeRate);

                  return (
                    <>
                      {/* Original Totals */}
                      {totalUSD > 0 && (
                        <div className="total-row primary">
                          <strong>{t('totalUSD')}:</strong>
                          <strong>${totalUSD.toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
                        </div>
                      )}
                      {totalLBP > 0 && (
                        <div className="total-row primary">
                          <strong>{t('totalLBP')}:</strong>
                          <strong>{totalLBP.toLocaleString('en-US', { minimumFractionDigits: 2 })} {t('lbpSymbol')}</strong>
                        </div>
                      )}

                      {/* Grand Totals */}
                      {(totalUSD > 0 || totalLBP > 0) && (
                        <>
                          <div className="conversion-divider"></div>
                          <div className="total-row grand">
                            <strong>{t('grandTotal')}:</strong>
                            <strong>${grandTotalUSD.toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
                          </div>
                          <div className="total-row grand">
                            <strong>{t('grandTotal')}:</strong>
                            <strong>{grandTotalLBP.toLocaleString('en-US', { minimumFractionDigits: 2 })} {t('lbpSymbol')}</strong>
                          </div>
                        </>
                      )}
                    </>
                  );
                })()}
              </div>

              {/* Edit Form */}
              {isEditing && !isEditingItems ? (
                <div className="edit-form">
                  <div className="form-group">
                    <label>{t('paymentMethod')}:</label>
                    <select
                      value={editData.payment_method}
                      onChange={(e) => setEditData({ ...editData, payment_method: e.target.value })}
                      className="form-input"
                    >
                      <option value="cash">{t('cash')}</option>
                      <option value="card">{t('card')}</option>
                      <option value="both">{t('both')}</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>{t('notes')}:</label>
                    <textarea
                      value={editData.notes}
                      onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                      className="form-input"
                      rows="3"
                      placeholder={t('enterNotes') || 'Enter notes...'}
                    />
                  </div>

                  <div className="form-actions">
                    <button onClick={handleUpdateReceipt} disabled={loading} className="save-btn">
                      {loading ? `${t('saving')}...` : t('save')}
                    </button>
                    <button onClick={() => setIsEditing(false)} className="cancel-btn">
                      {t('cancel')}
                    </button>
                  </div>
                </div>
              ) : isEditingItems ? (
                <div className="edit-form">
                  <div className="form-actions">
                    <button onClick={handleUpdateReceipt} disabled={loading} className="save-btn">
                      {loading ? `${t('saving')}...` : t('save')}
                    </button>
                    <button onClick={() => {
                      setIsEditingItems(false);
                      setEditItems(receipt.items.map(item => ({ ...item })));
                    }} className="cancel-btn">
                      {t('cancel')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="receipt-details" dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
                  <div className="detail-row">
                    <span>{t('paymentMethod')}:</span>
                    <strong>{t(receipt.payment_method)}</strong>
                  </div>
                  {receipt.notes && (
                    <div className="detail-row">
                      <span>{t('notes')}:</span>
                      <strong>{receipt.notes}</strong>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Receipts;
